import { useCallback, useEffect, useRef } from "react"
import { useViewerStore } from "store/viewer.store"
import { changeViewerTool, toggleRulerTool, type TViewerTool } from "../core/viewerTools"
import type { MutableRefObject } from "react"
import type { WebViewerInstance } from "@pdftron/webviewer"
import type { TQuotationZoomDirection, TViewerMode } from "store/viewer.store"
import type { TPaneBinding } from "../core/viewerPanes"

// How much one click-to-zoom step scales the current zoom, and how far the pointer may travel
// before a press counts as a drag (a marquee zoom) rather than a click.
const CLICK_ZOOM_FACTOR = 1.25
const CLICK_ZOOM_DRAG_TOLERANCE_PX = 4

type Props = {
	instanceRef: MutableRefObject<WebViewerInstance | null>
	mode: TViewerMode
	isInitialized: boolean
	// Re-resolved on every use; never cache the returned scroll elements across a layoutEpoch bump.
	getViewerPanes: () => TPaneBinding[]
	// Bumped whenever Apryse re-mounts the document area (split enter/exit) — the re-bind signal.
	layoutEpoch: number
}

// The canvas tools (select / pan / zoom / ruler): the commands the header buttons and hotkeys call,
// plus the per-pane pointer behavior the pan and zoom tools need. Everything here is bound to EVERY
// live pane (REQ-399 split screen), because Apryse's MultiViewer mode runs a second, fully
// independent DocumentViewer — see core/viewerTools for why a tool must be resolved per viewer.
export const useCanvasTools = ({ instanceRef, mode, isInitialized, getViewerPanes, layoutEpoch }: Props) => {
	const setQuotationZoomDirection = useViewerStore((state) => state.setQuotationZoomDirection)
	const quotationZoomDirection = useViewerStore((state) => state.quotationZoomDirection)
	// Read inside pointer handlers that must not re-bind when the direction flips.
	const quotationZoomDirectionRef = useRef(quotationZoomDirection)
	quotationZoomDirectionRef.current = quotationZoomDirection

	const onToolChange = useCallback(
		(tool: TViewerTool, zoomDirection: TQuotationZoomDirection = "in") => {
			if (!instanceRef.current) return
			changeViewerTool(instanceRef.current, tool, setQuotationZoomDirection, zoomDirection)
		},
		[instanceRef, setQuotationZoomDirection],
	)

	// Z activates zoom-in; Shift+Z activates zoom-out, and flips the direction when zoom is already on.
	const onZoomTool = useCallback(
		(event: KeyboardEvent) => {
			const instance = instanceRef.current
			if (!instance) return
			event.preventDefault()
			const { documentViewer, Tools } = instance.Core
			const isZoomActive = documentViewer.getToolMode() === documentViewer.getTool(Tools.ToolNames.MARQUEE)
			if (event.shiftKey && isZoomActive) {
				onToolChange("zoom", quotationZoomDirectionRef.current === "in" ? "out" : "in")
			} else if (event.shiftKey) {
				onToolChange("zoom", "out")
			} else {
				onToolChange("zoom", "in")
			}
		},
		[instanceRef, onToolChange],
	)

	const onToggleRulerTool = useCallback(() => {
		if (!instanceRef.current) return
		toggleRulerTool(instanceRef.current)
	}, [instanceRef])

	// Hide the furniture labels while the user pans. The hiding class lives on the shared .App root,
	// so a drag in either pane hides both panes' labels — but the pan tool state and the drag itself
	// are read from the pane the pointer is actually in.
	useEffect(() => {
		if (mode !== "editor" || !isInitialized || !instanceRef.current) return
		const { Tools } = instanceRef.current.Core
		const panes = getViewerPanes()
		if (panes.length === 0) return

		const appEl = panes[0].scrollEl.closest(".App") as HTMLElement | null
		let panDragHidingLabels = false
		const closeAnnotationContentOverlay = () => {
			instanceRef.current?.UI.closeElements(["annotationContentOverlay"])
		}
		const restoreLabels = () => {
			panDragHidingLabels = false
			appEl?.classList.remove("wv-pan-dragging")
		}

		const bindings = panes.map(({ documentViewer, scrollEl }) => {
			const isPanToolActive = () => documentViewer.getToolMode() === documentViewer.getTool(Tools.ToolNames.PAN)
			const hideLabelsForPan = (event: MouseEvent) => {
				if (event.button !== 0 || !isPanToolActive()) return
				panDragHidingLabels = true
				appEl?.classList.add("wv-pan-dragging")
				closeAnnotationContentOverlay()
			}
			const syncLabelsAfterToolChange = () => {
				if (isPanToolActive()) return
				restoreLabels()
			}
			const syncLabelsAfterPanMove = (event: MouseEvent) => {
				if (!isPanToolActive()) return
				if (panDragHidingLabels || event.buttons !== 0) {
					closeAnnotationContentOverlay()
					return
				}
				appEl?.classList.remove("wv-pan-dragging")
			}
			scrollEl.addEventListener("mousedown", hideLabelsForPan, true)
			scrollEl.addEventListener("mousemove", syncLabelsAfterPanMove, true)
			documentViewer.addEventListener("toolModeUpdated", syncLabelsAfterToolChange)
			return () => {
				scrollEl.removeEventListener("mousedown", hideLabelsForPan, true)
				scrollEl.removeEventListener("mousemove", syncLabelsAfterPanMove, true)
				documentViewer.removeEventListener("toolModeUpdated", syncLabelsAfterToolChange)
			}
		})

		const markPanMouseUp = () => {
			restoreLabels()
		}
		window.addEventListener("mouseup", markPanMouseUp, true)
		window.addEventListener("blur", restoreLabels, true)
		return () => {
			bindings.forEach((unbind) => unbind())
			window.removeEventListener("mouseup", markPanMouseUp, true)
			window.removeEventListener("blur", restoreLabels, true)
			restoreLabels()
		}
	}, [mode, isInitialized, instanceRef, getViewerPanes, layoutEpoch])

	// Click-to-zoom for the marquee/zoom tool, bound per pane so a click zooms the pane it landed in
	// and leaves the other one alone.
	useEffect(() => {
		if (mode !== "editor" || !isInitialized || !instanceRef.current) return
		const instance = instanceRef.current
		const { Tools } = instance.Core
		const panes = getViewerPanes()
		if (panes.length === 0) return

		const bindings = panes.map(({ documentViewer, scrollEl }) => {
			let zoomMouseDown: { clientX: number; clientY: number; zoomOut: boolean } | null = null

			const isZoomToolActive = () => documentViewer.getToolMode() === documentViewer.getTool(Tools.ToolNames.MARQUEE)

			const zoomAtMousePoint = (event: MouseEvent, shouldZoomOut: boolean) => {
				const currentZoom = documentViewer.getZoomLevel()
				if (currentZoom <= 0) return

				const scrollRect = scrollEl.getBoundingClientRect()
				const clickX = event.clientX - scrollRect.left
				const clickY = event.clientY - scrollRect.top
				const contentX = scrollEl.scrollLeft + clickX
				const contentY = scrollEl.scrollTop + clickY
				const nextZoom = currentZoom * (shouldZoomOut ? 1 / CLICK_ZOOM_FACTOR : CLICK_ZOOM_FACTOR)
				const clampedZoom = Math.max(instance.UI.getMinZoomLevel(), Math.min(instance.UI.getMaxZoomLevel(), nextZoom))
				if (clampedZoom === currentZoom) return

				// Per-viewer, NOT instance.UI.setFitMode: that API resolves to document viewer 1 only
				// (Apryse's core/fitToZoom defaults documentViewerKey to 1), so it would silently reset
				// the LEFT pane's fit mode when zooming the right one.
				documentViewer.setFitMode(documentViewer.FitMode.Zoom)
				documentViewer.zoomTo(clampedZoom)

				window.requestAnimationFrame(() => {
					const zoomRatio = clampedZoom / currentZoom
					scrollEl.scrollLeft = contentX * zoomRatio - clickX
					scrollEl.scrollTop = contentY * zoomRatio - clickY
				})
			}

			const onMouseDown = (event: MouseEvent) => {
				if (!isZoomToolActive()) return
				if (event.button !== 0) return
				zoomMouseDown = {
					clientX: event.clientX,
					clientY: event.clientY,
					zoomOut: quotationZoomDirectionRef.current === "out" || event.altKey || event.shiftKey || event.ctrlKey,
				}
				event.preventDefault()
				event.stopPropagation()
				event.stopImmediatePropagation()
			}

			const onMouseUp = (event: MouseEvent) => {
				if (!zoomMouseDown || !isZoomToolActive()) return
				const deltaX = Math.abs(event.clientX - zoomMouseDown.clientX)
				const deltaY = Math.abs(event.clientY - zoomMouseDown.clientY)
				const wasDrag = deltaX > CLICK_ZOOM_DRAG_TOLERANCE_PX || deltaY > CLICK_ZOOM_DRAG_TOLERANCE_PX
				const shouldZoomOut =
					zoomMouseDown.zoomOut ||
					quotationZoomDirectionRef.current === "out" ||
					event.altKey ||
					event.shiftKey ||
					event.ctrlKey
				zoomMouseDown = null
				event.preventDefault()
				event.stopPropagation()
				event.stopImmediatePropagation()
				if (wasDrag) return

				zoomAtMousePoint(event, shouldZoomOut)
			}

			scrollEl.addEventListener("mousedown", onMouseDown, true)
			scrollEl.addEventListener("mouseup", onMouseUp, true)
			return () => {
				scrollEl.removeEventListener("mousedown", onMouseDown, true)
				scrollEl.removeEventListener("mouseup", onMouseUp, true)
			}
		})

		return () => bindings.forEach((unbind) => unbind())
	}, [mode, isInitialized, instanceRef, getViewerPanes, layoutEpoch])

	// Zoom-in / zoom-out cursor on every pane's scroll element. Lives here rather than in the header
	// because Apryse re-roots the header on each of its own re-renders, while this has to track the
	// live scroll elements across split enter/exit (hence layoutEpoch).
	useEffect(() => {
		if (!isInitialized || !instanceRef.current) return
		const { Tools } = instanceRef.current.Core
		const panes = getViewerPanes()
		if (panes.length === 0) return

		const bindings = panes.map(({ documentViewer, scrollEl }) => {
			const syncZoomCursor = () => {
				const isZoomToolActive = documentViewer.getToolMode() === documentViewer.getTool(Tools.ToolNames.MARQUEE)
				scrollEl.classList.toggle("wv-zoom-in-cursor", isZoomToolActive && quotationZoomDirection === "in")
				scrollEl.classList.toggle("wv-zoom-out-cursor", isZoomToolActive && quotationZoomDirection === "out")
			}

			syncZoomCursor()
			documentViewer.addEventListener("toolModeUpdated", syncZoomCursor)
			return () => {
				scrollEl.classList.remove("wv-zoom-in-cursor")
				scrollEl.classList.remove("wv-zoom-out-cursor")
				documentViewer.removeEventListener("toolModeUpdated", syncZoomCursor)
			}
		})

		return () => bindings.forEach((unbind) => unbind())
		// The direction itself is a dep (not just the ref) so flipping in/out re-paints immediately.
	}, [isInitialized, instanceRef, getViewerPanes, layoutEpoch, quotationZoomDirection])

	return { onToolChange, onZoomTool, onToggleRulerTool }
}
